import { TranslationKey } from './../../../../../types/translation/TranslationKey';
import { useMutation } from '@apollo/client';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnboardingRoutes } from '../..';
import { ParseFileResponse, ParseFileVariables, OnboardingMutations } from '../../../../../graphql/onboarding.graphql';
import { CsvUploadType, fileNameToFileType } from '../../../../../types/csvUploadConfiguration';
import { t } from '../../../../../types/translation/Translator';
import { Button, ButtonVariant } from '../../../../components/Button/Button';
import { FileInput } from '../../../../components/File';
import FileItem from '../../../../components/File/FileItem';
import Icon, { IconType } from '../../../../icons/Icon';
import { LoadingSpinner } from '../../../Common/LoadingSpinner';
import { OnboardingStep, OnboardingTitle, OnboardingSubtitle } from '../../components';
import OnboardingPaneHeader from '../../components/OnboardingPaneHeader';
import { OnboardingData, flattenOnboardingEntities, mapParsedFileData } from '../../model/OnboardingData';
import { ToastType } from '../../../../components/Toast';
import { useToast } from '../../../../../providers/ToastProvider';
import { FileUtils } from '../../../../../types/file';

interface OnboardingReferenceDataImportPaneProps {
  data: OnboardingData;
  setData: (data: OnboardingData) => void;
}

export default function OnboardingReferenceDataImportPane({ data, setData }: OnboardingReferenceDataImportPaneProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const pmdFiles = useMemo(() => {
    return [...data.files.entries()].filter(([id, file]) => file.type === CsvUploadType.productMasterData);
  }, [data]);

  const [upload, { loading }] = useMutation<ParseFileResponse, ParseFileVariables>(OnboardingMutations.parseFile);

  const handleFile = async (files: File[] | FileList | null) => {
    if (!files) return;
    const file = files instanceof File ? files : files[0];

    await upload({
      variables: {
        input: {
          companyId: data.company.id,
          stockLocationId: '',
          type: CsvUploadType.productMasterData,
          deleteExisting: false,
          csvFileAsBase64Data: await FileUtils.fileToBase64(file),
          fileType: fileNameToFileType(file.name),
        },
        state: flattenOnboardingEntities(data.summary),
      },
      onError: error => {
        showToast({ title: error.message, type: ToastType.error });
      },
      onCompleted: response => {
        setData(
          data.import({
            file: { name: file.name, size: file.size },
            data: mapParsedFileData(response.parseFile),
            type: CsvUploadType.productMasterData,
          }),
        );
      },
    });
  };
  return (
    <div className='grid gap-4 overflow-hidden'>
      <OnboardingPaneHeader back={() => navigate(OnboardingRoutes.stockLocationOverview)} />

      <div className='grid gap-1'>
        <OnboardingStep />
        <OnboardingTitle text={'Add your Reference or Master Data'} />
        <OnboardingSubtitle
          text={
            'Provide the essential foundation for your system by adding Reference or Master Data. This includes key datasets such as product lists, customer records, supplier details, or other critical information.'
          }
        />
      </div>

      <p className='text-sm text-black'>
        Before you upload your file, make sure your file is{' '}
        <a className='text-blue-600' href={''}>
          ready to be imported
          <span className='ml-1 inline-block'>
            <Icon type={IconType.share4} stroke={'stroke-black'} size={'h-4 w-4'} />
          </span>
        </a>
      </p>

      {!pmdFiles.length ? (
        <div className='grid gap-2'>
          <p className='text-sm font-medium text-gray-900'>Upload your file to add your reference data</p>
          <FileInput onFiles={handleFile} acceptedTypes={'.csv,.xlsx'} />
        </div>
      ) : null}

      {pmdFiles.length ? (
        <div className='flex items-center gap-2 stroke-blue-600'>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <Icon type={IconType.checkCircle} stroke={'stroke-success-500'} size={'h-4 w-4'} />
          )}
          <p className='text-sm text-gray-600'>{pmdFiles[0][1].data.productMasterData.size} Reference Data added</p>
        </div>
      ) : null}

      {pmdFiles.length ? (
        <div className='flex flex-grow flex-col gap-2 overflow-auto'>
          {pmdFiles.map(([id, file], index) => (
            <FileItem key={id} file={file.file} onRemove={() => data.files.delete(id) && setData(data.update())} />
          ))}
        </div>
      ) : null}

      <div className='flex gap-2'>
        <Button
          text={pmdFiles.length ? t(TranslationKey.next).singular.label : 'Skip'}
          onClick={() =>
            navigate(
              data.summary.productMasterData.size
                ? OnboardingRoutes.referenceDataOverview
                : OnboardingRoutes.stockImport,
            )
          }
        />
        <Button
          text={'Instead, you can add your Reference Data manually'}
          onClick={() => navigate(OnboardingRoutes.referenceDataAdd)}
          variant={ButtonVariant.linkPrimary}
        />
      </div>
    </div>
  );
}
